import Image from "next/image";
import Link from "next/link";
import Gutter from "ui-kit/Gutter";
import { FlexRow, FlexCol } from "ui-kit/Layout/Flex";
import { Typography } from "ui-kit/Typography";
import { capitalizeFirstLetter, formatCurrency } from "utils/utils";

import styles from "./ClinicDetails.module.css";
import Colors from "ui-kit/colors";
import { ClinicInfoOverlay } from "./ClinicInfoOverlay";
import { Icon } from "ui-kit";
import classNames from "classnames";
import { ClinicData, VetClinicMarker } from "pages/veterinarkartan";
import { PlaceDetails } from "./VetMap";

const Separator = () => <div className={styles.separator} />;

const formatOpeningHour = (openingHour: string) => {
  if (!openingHour) {
    return "";
  }

  return capitalizeFirstLetter(openingHour);
};

const PriceComparison = ({
  price,
  nationalPrice,
}: {
  price: number;
  nationalPrice: number;
}) => {
  const higherThanNational = price > nationalPrice;
  const percentageDiff = Math.abs(
    parseFloat(((price / nationalPrice - 1) * 100).toFixed(2))
  );
  return (
    <div
      className={classNames(styles.priceComparison, {
        [styles.higher]: higherThanNational,
        [styles.lower]: !higherThanNational,
      })}
    >
      <FlexRow alignItems="center" style={{ gap: 8 }}>
        <Image
          src={`/images/icons/price-${
            higherThanNational ? "increase" : "decrease"
          }.svg`}
          alt={higherThanNational ? "higher-than-median" : "lower-than-median"}
          width={24}
          height={24}
          style={{
            display: "flex",
          }}
        />
        <Typography
          variant="body2"
          color="black"
          style={{ fontFamily: "var(--font-eina-semi-bold)" }}
        >
          {higherThanNational ? "Högre snittkostnad" : "Lägre snittkostnad"}
        </Typography>
      </FlexRow>
      <Typography variant="body3" color="grey500">
        {`Veterinärens snittkostnad för en skada (${formatCurrency(price, {
          maximumFractionDigits: 0,
        })}) är `}
      </Typography>
      <Typography
        variant="body3"
        color="grey500"
        style={{ fontFamily: "var(--font-eina-semi-bold)" }}
      >{`${percentageDiff}% ${
        higherThanNational ? "högre" : "lägre"
      }`}</Typography>
      <Typography variant="body3" color="grey500">
        {` än snittkostnaden för en skada i Sverige (${formatCurrency(
          nationalPrice,
          {
            maximumFractionDigits: 0,
          }
        )}). `}
        Det är dock viktigt att notera att dessa snittkostnader påverkas av
        flera faktorer, såsom prisnivåer, vårdkomplexitet och datatillgång.
        Kliniker som erbjuder avancerad specialistvård och har jouröppettider
        har ofta en högre snittkostnad, vilket inte nödvändigtvis innebär högre
        vårdpriser.
      </Typography>
    </div>
  );
};

const Billables = ({
  medianAmount,
  billables,
}: {
  medianAmount: number;
  billables: { [key: string]: { median_amount: number } };
}) => {
  return (
    <div className={styles.billables} style={{ maxWidth: 360 }}>
      <FlexRow justifyContent="space-between">
        <Typography variant="body2" color="grey500">
          Snittkostnad skada
        </Typography>
        <Typography
          variant="body2"
          style={{ fontFamily: "var(--font-eina-semi-bold)" }}
        >
          {formatCurrency(medianAmount, {
            maximumFractionDigits: 0,
          })}
        </Typography>
      </FlexRow>
      {Object.entries(billables).map(([key, value]) => {
        if (
          [
            "direktreglering",
            "sedering",
            "provtagning",
            "recept",
            "akut",
            "undersökning",
            "vaccination",
            "librela",
          ].includes(key)
        ) {
          return null;
        }
        return (
          <FlexRow key={key} justifyContent="space-between">
            <Typography
              variant="body2"
              color="grey500"
              style={{ textTransform: "capitalize" }}
            >
              {key}
            </Typography>
            <Typography
              variant="body2"
              style={{ fontFamily: "var(--font-eina-semi-bold)" }}
            >
              {value.median_amount
                ? formatCurrency(value.median_amount, {
                    maximumFractionDigits: 0,
                  })
                : "-"}
            </Typography>
          </FlexRow>
        );
      })}
    </div>
  );
};

const ContactInfo = ({ selectedPlace }: { selectedPlace: PlaceDetails }) => {
  const openingHours = selectedPlace?.openingHours?.[new Date().getDay() - 1];
  return (
    <div className={styles.contactInfo}>
      <FlexRow alignItems="center" style={{ gap: 12 }}>
        <div className={styles.contactInfoIcon}>
          <Icon name="location-marker" color={Colors.grey400} size="L" />
        </div>
        <Typography variant="body2">{selectedPlace.address}</Typography>
      </FlexRow>
      <FlexRow alignItems="center" style={{ gap: 12 }}>
        <div className={styles.contactInfoIcon}>
          <Icon name="globe" color={Colors.grey400} size="L" />
        </div>
        {selectedPlace?.website && (
          <Link href={selectedPlace?.website} target="_blank">
            <Typography variant="body2" color="blue300">
              Hemsida
            </Typography>
          </Link>
        )}
      </FlexRow>
      <FlexRow alignItems="center" style={{ gap: 12 }}>
        <div className={styles.contactInfoIcon}>
          <Icon name="time-circle" color={Colors.grey400} size="L" />
        </div>
        <FlexCol>
          <Typography variant="body2" color="grey400">
            Öppettider
          </Typography>
          <Typography variant="body2">
            {openingHours ? formatOpeningHour(openingHours) : "-"}
          </Typography>
        </FlexCol>
      </FlexRow>
      <FlexRow alignItems="center" style={{ gap: 12 }}>
        <div className={styles.contactInfoIcon}>
          <Icon name="phone" color={Colors.grey400} size="L" />
        </div>
        <Typography variant="body2">
          {selectedPlace.phoneNumber ?? "-"}
        </Typography>
      </FlexRow>
    </div>
  );
};

export const ClinicDetails = ({
  selectedPlace,
  selectedPlaceMarker,
  nationalData,
  setOverlayDisplay,
}: {
  selectedPlace: PlaceDetails;
  selectedPlaceMarker: VetClinicMarker;
  nationalData: ClinicData;
  setOverlayDisplay: (overlay: {
    title: string;
    content: React.ReactNode;
  }) => void;
}) => {
  if (!selectedPlace) {
    return null;
  }

  return (
    <div className={styles.clinicDetails}>
      <FlexCol style={{ gap: 2 }}>
        <FlexRow>
          <Typography
            variant="body1"
            style={{ fontFamily: "var(--font-eina-semi-bold)" }}
          >
            {selectedPlace.name}
          </Typography>
        </FlexRow>
        <FlexRow alignItems="center" style={{ gap: 8 }}>
          <Typography variant="body2" color="grey500">
            Google-betyg {selectedPlace.rating}
          </Typography>
          <Icon name="star" color={Colors.sun300} size="M" />
        </FlexRow>
        <FlexRow style={{ gap: 8 }}>
          <FlexRow style={{ maxWidth: "fit-content" }}>
            <Typography
              variant="body2"
              style={{ fontFamily: "var(--font-eina-semi-bold)" }}
              color={selectedPlace.openNow ? "forrest600" : "grey300"}
            >
              {selectedPlace.openNow ? "Öppet nu" : "Stängt"}
            </Typography>
          </FlexRow>
          {selectedPlaceMarker?.billables?.direktreglering && (
            <FlexRow
              alignItems="center"
              style={{
                gap: 8,
              }}
            >
              <Separator />
              <Icon name="checkmark" color={Colors.grey400} size="M" />
              <Typography variant="body2" color="grey500">
                Direktreglering
              </Typography>
            </FlexRow>
          )}
        </FlexRow>
      </FlexCol>
      <ContactInfo selectedPlace={selectedPlace} />
      <Gutter size={2} />
      {selectedPlaceMarker?.median_amount ? (
        <>
          <FlexRow
            alignItems="center"
            style={{ gap: 8, paddingTop: 4, paddingBottom: 4 }}
          >
            <Typography
              variant="body2"
              color="grey500"
              style={{ fontFamily: "var(--font-eina-semi-bold)" }}
            >
              Snittkostnader
            </Typography>
            <Image
              src="/images/icons/question-circle.svg"
              alt="external-link"
              width={20}
              height={20}
              style={{
                display: "flex",
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setOverlayDisplay({
                  title: "Prisinformation",
                  content: <ClinicInfoOverlay />,
                });
              }}
            />
          </FlexRow>
          <Typography variant="body3" color="grey500">
            Listade kostnader är snittkostnader, baserat på registrerade skador
            hos Lassie.
          </Typography>
          <Gutter size={2} />
          <Billables
            medianAmount={selectedPlaceMarker.median_amount}
            billables={selectedPlaceMarker.billables ?? {}}
          />
          <Gutter size={2} />
          <PriceComparison
            price={selectedPlaceMarker.median_amount}
            nationalPrice={nationalData.median_amount ?? 0}
          />
        </>
      ) : (
        <Typography variant="body2" color="grey500">
          Tyvärr har vi inte kunnat hitta några snittkostnader för denna
          veterinär.
        </Typography>
      )}
    </div>
  );
};
